/**
 * Mermaid Tool - 配置與範本定義
 * static/js/mermaid/config.js
 */

// ============== 常數定義 ==============
export const ZOOM_LIMITS = {
    MIN: 0.1,
    MAX: 5,
    FIT_MAX: 2,
    STEP: 0.25
};

export const DEBOUNCE_DELAY = 500;

export const MAX_TITLE_LENGTH = 100;

// ============== 範本定義 ==============
export const TEMPLATES = {
    // 常用圖表
    flowchart: {
        name: '流程圖',
        code: `flowchart TD
    A[開始] --> B{條件判斷}
    B -->|是| C[執行動作 1]
    B -->|否| D[執行動作 2]
    C --> E[結束]
    D --> E`
    },
    gantt: {
        name: '甘特圖',
        code: `gantt
    title 專案時程
    dateFormat  YYYY-MM-DD
    section 規劃階段
    需求分析     :a1, 2024-01-01, 7d
    系統設計     :a2, after a1, 5d
    section 開發階段
    前端開發     :b1, after a2, 14d
    後端開發     :b2, after a2, 14d
    section 測試階段
    整合測試     :c1, after b1, 7d`
    },
    pie: {
        name: '圓餅圖',
        code: `pie title 瀏覽器市場份額
    "Chrome" : 65
    "Safari" : 19
    "Firefox" : 4
    "Edge" : 4
    "其他" : 8`
    },
    quadrant: {
        name: '象限圖',
        code: `quadrantChart
    title Reach and engagement of campaigns
    x-axis Low Reach --> High Reach
    y-axis Low Engagement --> High Engagement
    quadrant-1 We should expand
    quadrant-2 Need to promote
    quadrant-3 Re-evaluate
    quadrant-4 May be improved
    Campaign A: [0.3, 0.6]
    Campaign B: [0.45, 0.23]
    Campaign C: [0.57, 0.69]
    Campaign D: [0.78, 0.34]
    Campaign E: [0.40, 0.34]
    Campaign F: [0.35, 0.78]`
    },
    mindmap: {
        name: '心智圖',
        code: `mindmap
    root((專案規劃))
        目標
            提升效率
            降低成本
        資源
            人力
            預算
        時程
            第一季
            第二季`
    },
    timeline: {
        name: '時間線',
        code: `timeline
    title 公司發展歷程
    2020 : 公司成立
         : 獲得種子輪投資
    2021 : 產品上線
         : 用戶突破1萬
    2022 : A輪融資
         : 團隊擴展至50人
    2023 : 國際市場拓展
         : 用戶突破100萬`
    },
    xychart: {
        name: 'XY 圖表',
        code: `xychart-beta
    title "銷售趨勢分析"
    x-axis [1月, 2月, 3月, 4月, 5月, 6月]
    y-axis "銷售額 (萬)" 0 --> 100
    bar [30, 45, 60, 55, 70, 85]
    line [25, 40, 55, 50, 65, 80]`
    },
    radar: {
        name: '雷達圖',
        code: `%%{init: {"radar": {"axisLabelFontSize": 14}} }%%
radar-beta
    title 技能評估
    axis Communication, Technical, Leadership, Creativity, Teamwork
    curve a: 80, 90, 70, 85, 75
    curve b: 70, 85, 80, 60, 90`
    },
    
    // 進階圖表
    sequence: {
        name: '序列圖',
        code: `sequenceDiagram
    participant 用戶
    participant 系統
    participant 資料庫
    
    用戶->>系統: 登入請求
    系統->>資料庫: 驗證帳號
    資料庫-->>系統: 驗證結果
    系統-->>用戶: 登入成功`
    },
    state: {
        name: '狀態圖',
        code: `stateDiagram-v2
    [*] --> 待處理
    待處理 --> 進行中: 開始處理
    進行中 --> 已完成: 完成
    進行中 --> 待處理: 退回
    已完成 --> [*]`
    },
    classDiagram: {
        name: '類別圖',
        code: `classDiagram
    Animal <|-- Duck
    Animal <|-- Fish
    Animal : +int age
    Animal : +String gender
    Animal: +isMammal()
    Animal: +mate()
    class Duck{
        +String beakColor
        +swim()
        +quack()
    }
    class Fish{
        -int sizeInFeet
        -canEat()
    }`
    },
    erDiagram: {
        name: 'ER 圖',
        code: `erDiagram
    CUSTOMER ||--o{ ORDER : places
    ORDER ||--|{ LINE-ITEM : contains
    CUSTOMER {
        string name
        string custNumber
        string sector
    }
    ORDER {
        int orderNumber
        date created
    }
    LINE-ITEM {
        int quantity
        float pricePerUnit
    }`
    },
    journey: {
        name: '用戶旅程',
        code: `journey
    title 用戶購物體驗
    section 瀏覽
        進入網站: 5: 用戶
        搜尋商品: 4: 用戶
        查看詳情: 3: 用戶
    section 購買
        加入購物車: 4: 用戶
        結帳: 2: 用戶
        付款: 3: 用戶`
    },
    gitGraph: {
        name: 'Git 分支',
        code: `gitGraph
    commit
    commit
    branch develop
    checkout develop
    commit
    commit
    checkout main
    merge develop
    commit`
    },
    block: {
        name: '區塊圖',
        code: `block-beta
    columns 3
    a["前端"] b["API"] c["資料庫"]
    d["用戶"] e["服務"] f["存儲"]`
    },
    packet: {
        name: '封包圖',
        code: `packet-beta
    0-15: "Source Port"
    16-31: "Destination Port"
    32-63: "Sequence Number"
    64-95: "Acknowledgment Number"
    96-99: "Data Offset"
    100-105: "Reserved"
    106-111: "Flags"
    112-127: "Window Size"`
    },
    architecture: {
        name: '架構圖',
        code: `architecture-beta
    group cloud(cloud)[雲端服務]
    
    service frontend(server)[前端] in cloud
    service backend(server)[後端] in cloud
    service db(database)[資料庫] in cloud
    
    frontend:R --> L:backend
    backend:R --> L:db`
    }
};

// ============== 圖表類型圖示 ==============
export const DIAGRAM_ICONS = {
    flowchart: '📊',
    graph: '📊',
    sequence: '🔄',
    gantt: '📅',
    pie: '🥧',
    mindmap: '🧠',
    timeline: '📆',
    state: '🔀',
    class: '📦',
    er: '🗃️',
    journey: '🚶',
    git: '🌳',
    quadrant: '📐',
    xychart: '📈',
    radar: '📡',
    block: '🧱',
    packet: '📦',
    architecture: '🏗️',
    default: '📄'
};

/**
 * 獲取圖表類型對應的圖示
 * @param {string} diagramType - 圖表類型
 * @returns {string} 圖示 emoji
 */
export function getDiagramIcon(diagramType) {
    return DIAGRAM_ICONS[diagramType] || DIAGRAM_ICONS.default;
}
